import { apiClient } from '../utils/apiClient';
import type {
    Session,
    CreateSessionRequest,
    UpdateSessionRequest,
    UpdateSessionParticipantsRequest,
    SessionTemplate,
    SessionTemplateStep,
    SessionQueryParams,
    PaginatedSessionsResponse,
} from '@arrowise/shared';
import { exportSessionZip } from './exportService';

// Response shapes as returned by the backend routes
interface SessionResponse { session: Session }
interface UpdateParticipantsResponse { session: Session }

interface TemplatesResponse { templates: (SessionTemplate & { steps?: SessionTemplateStep[] })[] }
interface TemplateResponse { template: SessionTemplate & { steps?: SessionTemplateStep[] } }

class SessionService {
    // Create a session (individual or team)
    async createSession(payload: CreateSessionRequest): Promise<Session> {
        const response = await apiClient.post<SessionResponse>('/sessions', payload);
        if (!response.data?.session) throw new Error('Failed to create session');
        return response.data.session;
    }

    // Get a single session by id (candidate info is part of session)
    async getSessionById(sessionId: string): Promise<Session> {
        const response = await apiClient.get<SessionResponse>(`/sessions/${sessionId}`);
        if (!response.data?.session) throw new Error('Failed to fetch session');
        return response.data.session;
    }

    // Get grouped sessions for current user (incomplete vs complete)
    async getGroupedSessionsForUser(query?: SessionQueryParams): Promise<PaginatedSessionsResponse> {
        const params = query ? {
            status: query.status,
            search: query.search,
            page: query.page,
            limit: query.limit,
            sortBy: query.sortBy,
            sortOrder: query.sortOrder,
            positionId: query.positionId,
        } : undefined;
        const response = await apiClient.get<PaginatedSessionsResponse>('/sessions/me/grouped', params);
        if (!response.data) throw new Error('Failed to fetch sessions');
        return response.data;
    }

    // Get grouped sessions for a team (incomplete vs complete)
    async getGroupedSessionsForTeam(teamId: string, query?: SessionQueryParams): Promise<PaginatedSessionsResponse> {
        const params = query ? {
            status: query.status,
            search: query.search,
            page: query.page,
            limit: query.limit,
            sortBy: query.sortBy,
            sortOrder: query.sortOrder,
            positionId: query.positionId,
        } : undefined;
        const response = await apiClient.get<PaginatedSessionsResponse>(`/sessions/team/${teamId}/grouped`, params);
        if (!response.data) throw new Error('Failed to fetch team sessions');
        return response.data;
    }

    // Update session details (title/description/language/status/dueAt)
    async updateSession(sessionId: string, payload: UpdateSessionRequest): Promise<Session> {
        const response = await apiClient.put<SessionResponse>(`/sessions/${sessionId}`, payload);
        if (!response.data?.session) throw new Error('Failed to update session');
        return response.data.session;
    }

    // Update candidate for a team session
    async updateParticipants(sessionId: string, payload: UpdateSessionParticipantsRequest): Promise<Session> {
        const response = await apiClient.put<UpdateParticipantsResponse>(`/sessions/${sessionId}/participants`, payload);
        if (!response.data?.session) throw new Error('Failed to update participants');
        return response.data.session;
    }

    // Delete a session
    async deleteSession(sessionId: string): Promise<void> {
        const response = await apiClient.delete<{ success: boolean }>(`/sessions/${sessionId}`);
        if (!response.success) throw new Error(response.message || 'Failed to delete session');
    }

    // Helper: is the session still before its due time? (async model)
    isJoinWindowOpen(session: Session, now: Date = new Date()): boolean {
        if (!session.dueAt) return true; // no due date means open
        const due = new Date(session.dueAt).getTime();
        const nowMs = now.getTime();
        return nowMs <= due;
    }

    // List session templates (public and optional team-specific)
    async listTemplates(teamId?: string | null): Promise<SessionTemplate[]> {
        const params = teamId ? { teamId } : undefined;
        const response = await apiClient.get<TemplatesResponse>('/sessions/templates', params);
        if (!response.data?.templates) throw new Error('Failed to fetch templates');
        return response.data.templates;
    }

    // Get all session templates for a user and team context
    async getAllTemplates(teamId?: string | null): Promise<(SessionTemplate & { steps?: SessionTemplateStep[] })[]> {
        const params = teamId ? { teamId } : undefined;
        const response = await apiClient.get<TemplatesResponse>('/sessions/all-templates', params);
        if (!response.data?.templates) throw new Error('Failed to fetch templates');
        return response.data.templates;
    }

    // Get a specific session template with its steps
    async getTemplateById(templateId: string, teamId?: string | null): Promise<SessionTemplate & { steps?: SessionTemplateStep[] }> {
        const params = teamId ? { teamId } : undefined;
        const response = await apiClient.get<TemplateResponse>(`/sessions/templates/${templateId}`, params);
        if (!response.data?.template) throw new Error('Failed to fetch template');
        return response.data.template;
    }

    // Resend candidate invite email for a session
    async resendCandidateInviteEmail(sessionId: string): Promise<void> {
        const response = await apiClient.post<{ success: boolean; message?: string }>(`/sessions/${sessionId}/resend-candidate-email`);
        if (!response.success) throw new Error(response.message || 'Failed to resend candidate invite email');
    }

    // Export session (client builds zip)
    async exportSession(sessionId: string): Promise<void> {
        return exportSessionZip(sessionId);
    }
}

export const sessionService = new SessionService();
export default sessionService;
